// frontend/src/admin/admindashboard.js
// Main Admin Dashboard - Central hub for all admin functionalities

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TeacherDashboard from '../quiz_management/quizteacherdashboard';
import './admindashboard.css';
import StudentManagement from './studentmanagment'; // Note: your file is named 'studentmanagment.js' (missing 'e')
import AccessControlManagement from './accesscontrolmanagement';
import BulkUploadManager from '../bulk_upload/BulkUploadManager';
import AdminStudyMaterial from '../study_material/AdminStudyMaterial';
import TeacherManagement from './teachermanagement';
import AdminContentNavigator from './AdminContentNavigator';

// Import management components (you'll create these files)
// import StudentManagement from './studentmanagement';
import SubjectManagement from './subjectmanagement'; 
import ChapterManagement from './chaptermanagement';
import ContentManagement from './contentmanagement';
import QAManagement from './qamanagement';
import LLMProviderManagement from './llmprovidermanagement';
import AskAiSettings from './AskAiSettings';

const AdminDashboard = () => {
  const [currentModule, setCurrentModule] = useState('dashboard');
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  

  const [dashboardData, setDashboardData] = useState({
    totalStudents: 0,
    totalSubjects: 0,
    activeQuizzes: 0,
    pendingDoubts: 0
  });
  const [dataLoading, setDataLoading] = useState(true);
  
  const navigate = useNavigate();

  // ADD THIS FUNCTION HERE (inside the function)
  const loadDashboardData = async () => {
    try {
      setDataLoading(true);
      
      // API calls to get dashboard data
      const API_BASE = process.env.REACT_APP_API_BASE || 'http://80.225.197.31:8001';
      
      const studentsResponse = await fetch(`${API_BASE}/students/admin-count`);
      const subjectsResponse = await fetch(`${API_BASE}/subjects/admin-count`);
      //const quizzesResponse = await fetch(`${API_BASE}/quizzes/active/count`);
      //const doubtsResponse = await fetch(`${API_BASE}/doubts/pending/count`);
      
      const data = {
        totalStudents: studentsResponse.ok ? (await studentsResponse.json()).count : 0,
        totalSubjects: subjectsResponse.ok ? (await subjectsResponse.json()).count : 0,
        activeQuizzes: 0, // Hardcode for now
        pendingDoubts: 0  // Hardcode for now
      };

      console.log('Dashboard data:', data); // Add this to debug
      
      setDashboardData(data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setDataLoading(false);
    }
  };
  
  const checkAdminAuth = () => {
    const adminLoggedIn = sessionStorage.getItem('adminLoggedIn');
    const adminData = sessionStorage.getItem('adminData');
    
    if (adminLoggedIn === 'true' && adminData) {
      setAdmin(JSON.parse(adminData));
    } else {
      setAdmin(null);
      navigate('/admin/login');
    }
    setLoading(false);
  };
  // ADD THIS useEffect HERE (inside the function)
  useEffect(() => {
    if (currentModule === 'dashboard') {
      loadDashboardData();
    }
  }, [currentModule]);

  useEffect(() => {
    checkAdminAuth();
  }, []);
   
  // Admin modules configuration
  const adminModules = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: '📊',
      description: 'Overview and statistics',
      category: 'overview'
    },
    {
      id: 'student-management',
      title: 'Student Management',
      icon: '👥',
      description: 'Add, edit, delete students and manage access',
      category: 'users'
    },

    {
      id: 'teacher-management',
      title: 'Teacher Management',
      icon: '👨‍🏫',
      description: 'Add, activate, deactivate teacher accounts',
      category: 'users'
    },
    {
      id: 'content-navigator',
      title: 'Content Editor (New)',
      icon: '🧩',
      description: 'Block-based content editor for topics, subtopics, chunks, micro chunks',
      category: 'content',
      route: '/admin/content-editor',
      openInNewTab: true
    },
    {
      id: 'subject-management',
      title: 'Subject Management',
      icon: '📚',
      description: 'Create and manage subjects',
      category: 'content'
    },
    {
      id: 'chapter-management',
      title: 'Chapter Management',
      icon: '📖',
      description: 'Create and manage chapters',
      category: 'content'
    },
    {
      id: 'quiz-management', 
      title: 'Quiz Management',
      icon: '📝',
      description: 'Create and manage quizzes',
      category: 'content'
    },

    {
      id: 'llm-providers',
      title: 'LLM Provider Management',
      icon: '🤖',
      description: 'Manage AI language model providers, monitor usage, and switch active providers',
      category: 'settings',
      status: 'active',
      route: '/admin/llm-providers'
    },
    
    {
      id: 'content-management',
      title: 'Course Builder',
      icon: '📚',
      description: 'Manage topics, subtopics, chunks, and micro chunks',
      category: 'content',
      route: '/admin/form-tree-editor', // ADD THIS
      openInNewTab: true // ADD THIS
    },

     {
      id: 'bulk-upload',
      title: 'Excel Bulk Upload',
      icon: '📊',
      description: 'Bulk upload chunks, micro-chunks, and explanations via Excel',
      category: 'content',
      status: 'active'
    },

    {
      id: 'qa-management',
      title: 'Q&A Management', 
      icon: '💡',
      description: 'Create and manage predefined questions & answers',
      category: 'content'
    },

    {
      id: 'access-control',
      title: 'Access Control',
      icon: '🔐',
      description: 'Manage student access to subjects and features',
      category: 'users',
      status: 'active',
      component: 'AccessControlManagement'
    },

    {
      id: 'ask-ai-config',
      title: 'Ask AI Settings',
      icon: '✦',
      description: 'Enable/disable student Ask AI per subject, block type and student',
      category: 'settings',
      status: 'active',
      component: 'AskAiSettings'
    },
    
    {
      id: 'progress-tracking',
      title: 'Progress Tracking',
      icon: '📈',
      description: 'Student progress and analytics',
      category: 'analytics'
    },
    {
      id: 'doubt-management',
      title: 'Doubt Management', 
      icon: '❓',
      description: 'Review and manage student doubts',
      category: 'content'
    },
    {
      id: 'study-material',
      title: 'Study Material',
      icon: '📂',
      description: 'Upload and manage study materials',
      category: 'content'
    },
    {
      id: 'system-settings',
      title: 'System Settings',
      icon: '⚙️',
      description: 'Configure system parameters',
      category: 'settings'
    }
  ];

  

  const handleLogout = () => {
    // Clear admin data and redirect
    setAdmin(null);
    sessionStorage.removeItem('adminLoggedIn');
    sessionStorage.removeItem('adminData');
    navigate('/admin/login');
  };

  const renderModuleContent = () => {
    switch (currentModule) {
      case 'dashboard':
        return renderOverviewDashboard();
      case 'student-management':
        return <StudentManagement onBack={() => setCurrentModule('dashboard')} />;
      case 'teacher-management':
        return <TeacherManagement onBack={() => setCurrentModule('dashboard')} />;
      case 'subject-management':
        return <SubjectManagement onBack={() => setCurrentModule('dashboard')} />;
      case 'chapter-management':
        return <ChapterManagement onBack={() => setCurrentModule('dashboard')} />;
      case 'content-management':
        return <ContentManagement onBack={() => setCurrentModule('dashboard')} />;
      case 'bulk-upload':
        return <BulkUploadManager onBack={() => setCurrentModule('dashboard')} />;
      case 'qa-management':
        return <QAManagement onBack={() => setCurrentModule('dashboard')} />;   
      case 'quiz-management':
        return <TeacherDashboard />; // Quiz management from teacher dashboard
      case 'llm-providers':
        return <LLMProviderManagement />;  
      case 'access-control':
        return <AccessControlManagement />;
      case 'progress-tracking':
        return renderProgressTracking();
      case 'doubt-management':
        return renderDoubtManagement();
      case 'study-material':
        return <AdminStudyMaterial />; 
      case 'content-navigator':
        return <AdminContentNavigator />;
      case 'ask-ai-config':
        return <AskAiSettings onBack={() => setCurrentModule('dashboard')} />;
        
      case 'system-settings':
        return renderSystemSettings();
      default:
        return renderOverviewDashboard();
    }
  };

  const renderOverviewDashboard = () => (
    <div className="overview-dashboard">
      <div className="overview-header">
        <h2>Admin Dashboard Overview</h2>
        <p>Welcome back, {admin?.name}! Here's what's happening with your Smart AI Tutor system.</p>
      </div>

      {/* Quick Stats */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>Total Students</h3>
            <p className="stat-number">{dataLoading ? '--' : dashboardData.totalStudents}</p>
            <span className="stat-change">{dataLoading ? 'Loading...' : 'Active Students'}</span>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">📚</div>
          <div className="stat-content">
            <h3>Subjects</h3>
            <p className="stat-number">{dataLoading ? '--' : dashboardData.totalSubjects}</p>
            <span className="stat-change">{dataLoading ? 'Loading...' : 'Available Subjects'}</span>
          </div>
        </div>

        
        <div className="stat-card warning">
          <div className="stat-icon">📝</div>
          <div className="stat-content">
            <h3>Active Quizzes</h3>
            <p className="stat-number">{dataLoading ? '--' : dashboardData.activeQuizzes}</p>
            <span className="stat-change">{dataLoading ? 'Loading...' : 'Currently Active'}</span>
          </div>
        </div>

        <div className="stat-card info">
          <div className="stat-icon">❓</div>
          <div className="stat-content">
            <h3>Pending Doubts</h3>
            <p className="stat-number">{dataLoading ? '--' : dashboardData.pendingDoubts}</p>
            <span className="stat-change">{dataLoading ? 'Loading...' : 'Awaiting Response'}</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <button 
            className="action-btn"
            onClick={() => setCurrentModule('student-management')}
          >
            <span>👥</span>
            <span>Manage Students</span>
          </button>
          <button 
            className="action-btn"
            onClick={() => setCurrentModule('subject-management')}
          >
            <span>📚</span>
            <span>Manage Subjects</span>
          </button>
          <button 
            className="action-btn"
            onClick={() => setCurrentModule('quiz-management')}
          >
            <span>📝</span>
            <span>Create Quiz</span>
          </button>
          <button 
            className="action-btn"
            onClick={() => setCurrentModule('access-control')}
          >
            <span>🔐</span>
            <span>Grant Access</span>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="recent-activity">
        <h3>Recent Activity</h3>
        <div className="activity-list">
          <div className="activity-item">
            <span className="activity-icon">👥</span>
            <span className="activity-text">New student registration pending approval</span>
            <span className="activity-time">2 hours ago</span>
          </div>
          <div className="activity-item">
            <span className="activity-icon">❓</span>
            <span className="activity-text">5 new doubts require review</span>
            <span className="activity-time">4 hours ago</span>
          </div>
          <div className="activity-item">
            <span className="activity-icon">📝</span>
            <span className="activity-text">Quiz completed by 12 students</span>
            <span className="activity-time">6 hours ago</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Placeholder components for future modules
  const renderStudentManagement = () => (
    <div className="module-placeholder">
      <h2>Student Management</h2>
      <p>This module will include:</p>
      <ul>
        <li>Create/Edit/Delete students</li>
        <li>Manage student profiles</li>
        <li>Bulk student operations</li>
        <li>Student status management</li>
      </ul>
      <p><em>Coming soon...</em></p>
    </div>
  );

  const renderProgressTracking = () => (
    <div className="module-placeholder">
      <h2>Progress Tracking</h2>
      <p>This module will include:</p>
      <ul>
        <li>Student learning progress</li>
        <li>Quiz performance analytics</li>
        <li>Time spent tracking</li>
        <li>Completion rates</li>
      </ul>
      <p><em>Coming soon...</em></p>
    </div>
  );

  const renderDoubtManagement = () => (
    <div className="module-placeholder">
      <h2>Doubt Management</h2>
      <p>This module will include:</p>
      <ul>
        <li>Review pending doubts</li>
        <li>Approve/edit AI responses</li>
        <li>Merge similar questions</li>
        <li>Doubt analytics</li>
      </ul>
      <p><em>Coming soon...</em></p>
    </div>
  );

  const renderSystemSettings = () => (
    <div className="module-placeholder">
      <h2>System Settings</h2>
      <p>This module will include:</p>
      <ul>
        <li>General system configuration</li>
        <li>Email settings</li>
        <li>AI model parameters</li>
        <li>Security settings</li>
      </ul>
      <p><em>Coming soon...</em></p>
    </div>
  );

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading Admin Dashboard...</p>
      </div>
    );
  }

  
  // Group modules by category
  const modulesByCategory = adminModules.reduce((acc, module) => {
    if (!acc[module.category]) {
      acc[module.category] = [];
    }
    acc[module.category].push(module);
    return acc;
  }, {});

  const categoryTitles = {
    overview: 'Overview',
    content: 'Content Management',
    users: 'User Management', 
    analytics: 'Analytics & Reports',
    settings: 'System Configuration'
  };

  return (
    <div className="admin-dashboard">
      {/* Sidebar Navigation */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <h2>Admin Panel</h2>
          <div className="admin-info">
            <span>{admin.name}</span>
            <span className="admin-role">{admin.role}</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {Object.entries(modulesByCategory).map(([category, modules]) => (
            <div key={category} className="nav-category">
              <h3>{categoryTitles[category]}</h3>
              <ul>
                {modules.map(module => (
                  <li key={module.id}>
                    <button
                      className={`nav-item ${currentModule === module.id ? 'active' : ''}`}
                      onClick={() => {
                        if (module.openInNewTab) {
                          window.open(module.route, '_blank');
                        } else {
                          setCurrentModule(module.id);
                        }
                      }}
                    >
                      <span className="nav-icon">{module.icon}</span>
                      <span className="nav-text">{module.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            🚪 Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <header className="admin-header">
          <div className="header-content">
            <h1>
              {adminModules.find(m => m.id === currentModule)?.title || 'Dashboard'}
            </h1>
            <p>
              {adminModules.find(m => m.id === currentModule)?.description || 'Admin overview'}
            </p>
          </div>
          <div className="header-actions">
            <button className="header-btn">
              🔔 Notifications
            </button>
            <button className="header-btn">
              ⚙️ Settings
            </button>
          </div>
        </header>

        <div className="admin-content">
          {renderModuleContent()}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;